import React, { useState, useEffect, useRef } from 'react';
import Navbar from './Navbar';
import { useParams, useNavigate } from 'react-router-dom';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import {
    FaEdit,
    FaClock,
    FaQuestionCircle,
    FaCoins,
    FaCalendarAlt,
    FaHeart,
    FaTrophy,
    FaImage,
    FaInfoCircle,
    FaStopwatch,
    FaBolt,
    FaEye,
    FaCheckCircle,
    FaTimesCircle,
    FaPauseCircle,
    FaStar,
    FaGem,
    FaFire,
    FaScroll,
    FaExternalLinkAlt,
    FaAngleDown,
    FaAngleUp,
    FaDownload,
    FaPrint,
    FaShareAlt
} from 'react-icons/fa';
import {
    MdSchedule,
    MdTimer,
    MdCategory,
    MdAccessTime,
    MdQuiz,
} from 'react-icons/md';
import {
    IoStatsChart,
    IoCalendar
} from 'react-icons/io5';
import {
    HiSparkles,
} from 'react-icons/hi';

// Rules Modal Component
const RulesModal = ({ isOpen, onClose, rulesUrl }) => {
    // Define hooks unconditionally at the top level
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Return early pattern - after hooks are defined
    if (!isOpen) return null;

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    const handleIframeLoad = () => {
        setIsLoading(false);
    };

    return (
        <div
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-fadeIn"
            onClick={onClose}
        >
            <div
                className={`bg-white rounded-2xl shadow-2xl flex flex-col transform transition-all duration-300 animate-scaleIn ${isFullscreen ? 'fixed inset-0 m-0 rounded-none' : 'w-full max-w-5xl max-h-[90vh]'
                    }`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center p-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-indigo-100 rounded-t-2xl">
                    <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <div className="p-2 bg-indigo-500 rounded-lg">
                            <FaScroll className="w-4 h-4 text-white" />
                        </div>
                        Quiz Rules
                    </h3>
                    <div className="flex items-center gap-2">
                        <a
                            href={rulesUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 bg-teal-500 hover:bg-teal-600 text-white rounded-lg flex items-center gap-2 transition-colors duration-200 text-sm"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <FaExternalLinkAlt className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Open in New Tab</span>
                        </a>
                        <button
                            onClick={toggleFullscreen}
                            className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 transition-colors duration-200 text-sm"
                            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                        >
                            {isFullscreen ? (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            ) : (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                                </svg>
                            )}
                            <span className="hidden sm:inline">{isFullscreen ? "Exit Fullscreen" : "Fullscreen"}</span>
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-red-50 text-red-500 hover:text-red-600 rounded-full transition-colors duration-200 flex items-center justify-center"
                            aria-label="Close modal"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="flex-1 overflow-hidden relative">
                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                            <div className="flex flex-col items-center">
                                <div className="animate-spin w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full mb-4"></div>
                                <p className="text-slate-600 text-sm font-medium">Loading rules...</p>
                            </div>
                        </div>
                    )}
                    <div className="absolute top-0 left-0 right-0 z-10 h-6 bg-gradient-to-b from-white to-transparent pointer-events-none"></div>
                    <div className="absolute bottom-0 left-0 right-0 z-10 h-6 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
                    <iframe
                        src={rulesUrl}
                        className="w-full h-[75vh] border-0"
                        title="Quiz Rules"
                        sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                        loading="lazy"
                        onLoad={handleIframeLoad}
                    />
                </div>
                <div className="p-3 bg-gradient-to-r from-indigo-50 to-blue-50 border-t border-indigo-100 rounded-b-2xl flex justify-between items-center">
                    <div className="text-sm text-slate-500 italic flex items-center">
                        <FaInfoCircle className="inline-block mr-1 text-indigo-500" />
                        <span className="hidden sm:inline">Viewing quiz rules and guidelines</span>
                    </div>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-lg transition-all duration-300 shadow-sm hover:shadow-md flex items-center gap-2 text-sm"
                    >
                        <span>Close</span>
                    </button>
                </div>
            </div>
        </div>
    );
};


const QuizView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loader, setLoader] = useState(false);
    const [quizData, setQuizData] = useState(null);
    const [showRulesModal, setShowRulesModal] = useState(false);
    const [isRulesExpanded, setIsRulesExpanded] = useState(false);
    const rulesPreviewRef = useRef(null);

    const handleEditQuiz = (id) => {
        navigate(`/manage/quiz/add?id=${id}`);
    };

    /* Add these styles to your CSS */
    const animationStyles = `
    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
    
    @keyframes scaleIn {
        from { transform: scale(0.95); opacity: 0; }
        to { transform: scale(1); opacity: 1; }
    }
    
    .animate-fadeIn {
        animation: fadeIn 0.3s ease-out;
    }
    
    .animate-scaleIn {
        animation: scaleIn 0.3s ease-out;
    }
    `;

    const fetchQuizData = async () => {
        setLoader(true);
        try {
            const response = await Calling.getQuiz('', id);
            if (response.status) {
                setQuizData(response.data);
            } else {
                toast.error('Failed to fetch quiz data');
            }
        } catch (error) {
            console.error('Error fetching quiz:', error);
            toast.error('Error fetching quiz data');
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchQuizData();
        }
    }, [id]);

    const getStatusIcon = (status) => {
        switch (status) {
            case 'active':
                return <FaCheckCircle className="w-4 h-4" />;
            case 'pending':
                return <FaPauseCircle className="w-4 h-4" />;
            case 'completed':
                return <FaCheckCircle className="w-4 h-4" />;
            default:
                return <FaTimesCircle className="w-4 h-4" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border-green-200';
            case 'pending':
                return 'bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-800 border-yellow-200';
            case 'completed':
                return 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border-blue-200';
            default:
                return 'bg-gradient-to-r from-red-100 to-red-100 text-red-800 border-red-200';
        }
    };

    const getDifficultyIcon = (difficulty) => {
        switch (difficulty) {
            case 'easy':
                return <FaCheckCircle className="w-5 h-5" />;
            case 'medium':
                return <FaStopwatch className="w-5 h-5" />;
            case 'hard':
                return <FaFire className="w-5 h-5" />;
            default:
                return <FaQuestionCircle className="w-5 h-5" />;
        }
    };

    const getRankIcon = (rank) => {
        switch (rank) {
            case 1:
                return <FaTrophy className="w-5 h-5 text-yellow-500" />;
            case 2:
                return <FaGem className="w-5 h-5 text-gray-500" />;
            case 3:
                return <FaStar className="w-5 h-5 text-orange-500" />;
            default:
                return <FaCoins className="w-5 h-5 text-blue-500" />;
        }
    };

    const getRankGradient = (rank, index) => {
        if (rank === 1) {
            return 'bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-300';
        } else if (rank === 2) {
            return 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-300';
        } else if (rank === 3) {
            return 'bg-gradient-to-r from-orange-50 to-orange-100 border-orange-300';
        } else {
            // For ranks beyond 3, use different color schemes
            const colors = [
                'bg-gradient-to-r from-blue-50 to-blue-100 border-blue-300',
                'bg-gradient-to-r from-green-50 to-green-100 border-green-300',
                'bg-gradient-to-r from-purple-50 to-purple-100 border-purple-300',
                'bg-gradient-to-r from-pink-50 to-pink-100 border-pink-300',
                'bg-gradient-to-r from-indigo-50 to-indigo-100 border-indigo-300'
            ];
            return colors[index % colors.length];
        }
    };

    const getRankButtonGradient = (rank, index) => {
        if (rank === 1) {
            return 'bg-gradient-to-r from-yellow-500 to-yellow-600';
        } else if (rank === 2) {
            return 'bg-gradient-to-r from-gray-400 to-gray-500';
        } else if (rank === 3) {
            return 'bg-gradient-to-r from-orange-500 to-orange-600';
        } else {
            // For ranks beyond 3, use different color schemes
            const colors = [
                'bg-gradient-to-r from-blue-500 to-blue-600',
                'bg-gradient-to-r from-green-500 to-green-600',
                'bg-gradient-to-r from-purple-500 to-purple-600',
                'bg-gradient-to-r from-pink-500 to-pink-600',
                'bg-gradient-to-r from-indigo-500 to-indigo-600'
            ];
            return colors[index % colors.length];
        }
    };

    const getRankIconColor = (rank, index) => {
        if (rank === 1) return 'text-yellow-600';
        if (rank === 2) return 'text-gray-600';
        if (rank === 3) return 'text-orange-600';

        const colors = ['text-blue-600', 'text-green-600', 'text-purple-600', 'text-pink-600', 'text-indigo-600'];
        return colors[index % colors.length];
    };

    const getRankTitle = (rank) => {
        if (rank === 1) return '1st Place';
        if (rank === 2) return '2nd Place';
        if (rank === 3) return '3rd Place';
        return `${rank}th Place`;
    };




    if (loader) {
        return (
            <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
                <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">Quiz Management</h1>
                <div className='bg-main-white rounded-xl shadow-lg'>
                    <Navbar selectState={'quizshow'} />
                    <hr className='border-main-border' />
                    <div className="flex justify-center items-center py-20">
                        <div className="text-center">
                            <div className="animate-spin w-16 h-16 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full mx-auto mb-6"></div>
                            <p className="text-slate-600 text-lg font-medium">Loading quiz details...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!quizData) {
        return (
            <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
                <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">Quiz Management</h1>
                <div className='bg-main-white rounded-xl shadow-lg p-6'>
                    <Navbar selectState={'quizshow'} />
                    <hr className='border-main-border mb-6' />
                    <div className="text-center py-20">
                        <FaQuestionCircle className="w-20 h-20 mx-auto mb-6 text-slate-400" />
                        <h3 className="text-2xl font-bold text-slate-600 mb-2">Quiz Not Found</h3>
                        <p className="text-slate-500">The requested quiz data is not available.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
            <style>{animationStyles}</style>
            <div className="flex justify-between items-center">
                <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">Quiz Management</h1>
            </div>
            <div className='bg-main-white rounded-xl shadow-lg'>
                <Navbar selectState={'quizshow'} />
                <hr className='border-main-border' />

                <div className="p-6">
                    {/* Header Section with Quiz Title and Edit Button */}
                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl p-6 mb-8 border border-indigo-100">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="p-2 bg-indigo-500 rounded-xl">
                                        <MdQuiz className="w-6 h-6 text-white" />
                                    </div>
                                    <h2 className="text-2xl md:text-3xl font-bold text-slate-800">{quizData.title}</h2>
                                </div>
                                <p className="text-slate-600 text-lg mb-4">{quizData.description}</p>
                                <div className="flex flex-wrap items-center gap-4">
                                    <div className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 ${getStatusColor(quizData.status)}`}>
                                        {getStatusIcon(quizData.status)}
                                        <span className="font-bold capitalize">{quizData.status}</span>
                                    </div>
                                    <div className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 ${quizData.type === 'daily'
                                        ? 'bg-gradient-to-r from-blue-100 to-blue-100 text-blue-800 border-blue-200'
                                        : 'bg-gradient-to-r from-red-100 to-red-100 text-red-800 border-red-200'
                                        }`}>
                                        {quizData.type === 'daily' ? <FaCalendarAlt className="w-4 h-4" /> : <FaBolt className="w-4 h-4" />}
                                        <span className="font-bold capitalize">{quizData.type} Quiz</span>
                                    </div>
                                    {quizData.rules_url && (
                                        <button
                                            onClick={() => setShowRulesModal(true)}
                                            className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 bg-gradient-to-r from-purple-100 to-purple-100 text-purple-800 border-purple-200 hover:from-purple-200 hover:to-purple-200 transition-all duration-300"
                                        >
                                            <FaScroll className="w-4 h-4" />
                                            <span className="font-bold">View Rules</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={() => handleEditQuiz(id)}
                                className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white px-6 py-3 rounded-xl flex items-center gap-3 transition-all duration-300 shadow-lg hover:shadow-xl font-semibold"
                            >
                                <FaEdit className="w-5 h-5" />
                                Edit Quiz
                            </button>
                        </div>
                    </div>

                    {/* Main Content Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Left Column - Quiz Details */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Timing Details */}
                            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl">
                                        <MdTimer className="w-6 h-6 text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-800">Timing Configuration</h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200">
                                        <div className="flex items-center gap-3 mb-2">
                                            <FaClock className="w-5 h-5 text-green-600" />
                                            <span className="font-semibold text-green-800">Quiz Duration</span>
                                        </div>
                                        <p className="text-2xl font-bold text-green-700">{quizData.quizTiming} minutes</p>
                                    </div>
                                    <div className="bg-gradient-to-r from-blue-50 to-blue-50 p-4 rounded-xl border border-blue-200">
                                        <div className="flex items-center gap-3 mb-2">
                                            <FaStopwatch className="w-5 h-5 text-blue-600" />
                                            <span className="font-semibold text-blue-800">Per Question</span>
                                        </div>
                                        <p className="text-2xl font-bold text-blue-700">{quizData.questionTiming} seconds</p>
                                    </div>
                                    <div className="bg-gradient-to-r from-orange-50 to-orange-50 p-4 rounded-xl border border-orange-200">
                                        <div className="flex items-center gap-3 mb-2">
                                            <IoStatsChart className="w-5 h-5 text-orange-600" />
                                            <span className="font-semibold text-orange-800">Result Time</span>
                                        </div>
                                        <p className="text-2xl font-bold text-orange-700">{quizData.resultTiming} minutes</p>
                                    </div>
                                    <div className="bg-gradient-to-r from-red-50 to-red-50 p-4 rounded-xl border border-red-200">
                                        <div className="flex items-center gap-3 mb-2">
                                            <FaHeart className="w-5 h-5 text-red-600" />
                                            <span className="font-semibold text-red-800">Life Lines</span>
                                        </div>
                                        <p className="text-2xl font-bold text-red-700">{quizData.lifeLine} Available</p>
                                    </div>
                                </div>
                                {quizData?.type === "live" && (
                                    <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-purple-50 rounded-xl border border-purple-200">
                                        <div className="flex items-center gap-3 mb-2">
                                            <MdSchedule className="w-5 h-5 text-purple-600" />
                                            <span className="font-semibold text-purple-800">Scheduled For</span>
                                        </div>
                                        <p className="text-lg font-bold text-purple-700">
                                            {new Date(quizData.scheduleTime).toLocaleString()}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Question Distribution */}
                            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl">
                                        <MdCategory className="w-6 h-6 text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-800">Question Distribution</h3>
                                </div>
                                <div className="grid grid-cols-3 gap-6 mb-6">
                                    <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border-2 border-green-200 hover:border-green-300 transition-all duration-300">
                                        <div className="flex justify-center mb-3">
                                            {getDifficultyIcon('easy')}
                                        </div>
                                        <p className="font-bold text-green-700 mb-2">Easy</p>
                                        <p className="text-3xl font-bold text-green-600">{quizData.questionDistribution.easy}</p>
                                    </div>
                                    <div className="text-center p-6 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl border-2 border-yellow-200 hover:border-yellow-300 transition-all duration-300">
                                        <div className="flex justify-center mb-3">
                                            {getDifficultyIcon('medium')}
                                        </div>
                                        <p className="font-bold text-yellow-700 mb-2">Medium</p>
                                        <p className="text-3xl font-bold text-yellow-600">{quizData.questionDistribution.medium}</p>
                                    </div>
                                    <div className="text-center p-6 bg-gradient-to-br from-red-50 to-red-50 rounded-xl border-2 border-red-200 hover:border-red-300 transition-all duration-300">
                                        <div className="flex justify-center mb-3">
                                            {getDifficultyIcon('hard')}
                                        </div>
                                        <p className="font-bold text-red-700 mb-2">Hard</p>
                                        <p className="text-3xl font-bold text-red-600">{quizData.questionDistribution.hard}</p>
                                    </div>
                                </div>
                                <div className="text-center p-4 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl border border-indigo-200">
                                    <div className="flex items-center justify-center gap-3">
                                        <FaQuestionCircle className="w-6 h-6 text-indigo-600" />
                                        <span className="text-xl font-bold text-indigo-800">
                                            Total Questions: {quizData.totalQuestion}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Rules Section - New Addition */}
                            {quizData.rules_url && (
                                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="p-3 bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl">
                                            <FaScroll className="w-6 h-6 text-white" />
                                        </div>
                                        <h3 className="text-xl font-bold text-slate-800">Quiz Rules</h3>
                                    </div>

                                    <div className={`p-6 bg-gradient-to-r from-teal-50 to-teal-50 rounded-xl border border-teal-200 flex flex-col items-center transition-all duration-300 ${isRulesExpanded ? 'max-h-[800px]' : 'max-h-[400px]'}`}>
                                        <div className="w-full flex flex-col md:flex-row items-center justify-between gap-4 mb-4">
                                            <div className="flex items-center gap-3">
                                                <FaInfoCircle className="w-6 h-6 text-teal-600" />
                                                <span className="font-semibold text-teal-800 text-lg">Quiz rules and guidelines</span>
                                            </div>

                                            <div className="flex flex-wrap gap-2">
                                                <a
                                                    href={quizData.rules_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="px-3 py-1.5 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-lg flex items-center gap-2 transition-all duration-300 shadow-sm hover:shadow-md text-sm"
                                                >
                                                    <FaExternalLinkAlt className="w-3.5 h-3.5" />
                                                    <span>Open in New Tab</span>
                                                </a>
                                                <button
                                                    onClick={() => setShowRulesModal(true)}
                                                    className="px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white rounded-lg flex items-center gap-2 transition-all duration-300 shadow-sm hover:shadow-md text-sm"
                                                >
                                                    <FaEye className="w-3.5 h-3.5" />
                                                    <span>Preview</span>
                                                </button>
                                                <button
                                                    onClick={() => setIsRulesExpanded(!isRulesExpanded)}
                                                    className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-lg flex items-center gap-2 transition-all duration-300 shadow-sm hover:shadow-md text-sm"
                                                >
                                                    {isRulesExpanded ? (
                                                        <>
                                                            <FaAngleUp className="w-3.5 h-3.5" />
                                                            <span>Collapse</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FaAngleDown className="w-3.5 h-3.5" />
                                                            <span>Expand</span>
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="w-full bg-white rounded-lg border border-teal-200 overflow-hidden transition-all duration-500" style={{ height: isRulesExpanded ? '600px' : '250px' }} ref={rulesPreviewRef}>
                                            <div className="h-full flex flex-col">
                                                <div className="flex justify-between items-center px-3 py-2 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 rounded-t-md">
                                                    <div className="flex items-center gap-2">
                                                        <FaScroll className="w-4 h-4 text-gray-600" />
                                                        <span className="text-sm font-medium text-gray-700">Rules Preview</span>
                                                    </div>
                                                </div>
                                                <div className="flex-1 overflow-hidden relative">
                                                    <iframe
                                                        src={quizData.rules_url}
                                                        className="w-full h-full border-0"
                                                        title="Quiz Rules Preview"
                                                        sandbox="allow-same-origin allow-scripts"
                                                        loading="lazy"
                                                    />
                                                    {!isRulesExpanded && (
                                                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white pointer-events-none"></div>
                                                    )}
                                                </div>
                                                {!isRulesExpanded && (
                                                    <button
                                                        onClick={() => setShowRulesModal(true)}
                                                        className="mt-2 w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md flex items-center justify-center gap-2 transition-colors duration-300"
                                                    >
                                                        <FaEye className="w-4 h-4" />
                                                        <span>View Full Rules</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right Column - Rewards and Image */}
                        <div className="space-y-6">
                            {/* Rewards Section */}
                            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-3 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-xl">
                                        <FaTrophy className="w-6 h-6 text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-800">Rewards</h3>
                                </div>

                                <div className="text-center mb-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-200">
                                    <div className="flex items-center justify-center gap-3 mb-2">
                                        <FaCoins className="w-8 h-8 text-yellow-600" />
                                        <span className="text-lg font-semibold text-yellow-800">Total Prize Pool</span>
                                    </div>
                                    <p className="text-3xl font-bold text-yellow-700">{quizData.totalCoin.toLocaleString()} coins</p>
                                </div>

                                <div>
                                    <h4 className="font-semibold mb-4 text-slate-700 flex items-center gap-2">
                                        <HiSparkles className="w-5 h-5 text-purple-600" />
                                        Prize Distribution ({quizData.winCoins.length} Ranks)
                                    </h4>
                                    <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                                        {quizData.winCoins.map((win, index) => (
                                            <div key={win._id} className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-300 hover:shadow-md ${getRankGradient(win.rank, index)}`}>
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white ${getRankButtonGradient(win.rank, index)}`}>
                                                        {win.rank}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <div className={getRankIconColor(win.rank, index)}>
                                                            {getRankIcon(win.rank)}
                                                        </div>
                                                        <span className="font-semibold text-slate-700">
                                                            {getRankTitle(win.rank)}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <FaCoins className={getRankIconColor(win.rank, index)} />
                                                    <span className="font-bold text-lg text-slate-800">{win.coin.toLocaleString()}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Summary Footer */}
                                    <div className="mt-4 p-3 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl border border-slate-200">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="font-semibold text-slate-600">
                                                Total Rewarded Ranks: {quizData.winCoins.length}
                                            </span>
                                            <span className="font-semibold text-slate-600">
                                                Prize Pool: {quizData.totalCoin.toLocaleString()} coins
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Quiz Image */}
                            {quizData.image && (
                                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl">
                                            <FaImage className="w-6 h-6 text-white" />
                                        </div>
                                        <h3 className="text-xl font-bold text-slate-800">Quiz Cover</h3>
                                    </div>
                                    <div className="relative overflow-hidden rounded-xl">
                                        <img
                                            src={quizData.image}
                                            alt="Quiz Cover"
                                            className="w-full h-auto rounded-xl shadow-lg hover:scale-105 transition-transform duration-300"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl"></div>
                                    </div>
                                </div>
                            )}

                            {/* Timestamps */}
                            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-3 bg-gradient-to-r from-slate-500 to-slate-600 rounded-xl">
                                        <FaInfoCircle className="w-6 h-6 text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-800">Timeline</h3>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                                        <IoCalendar className="w-5 h-5 text-slate-600" />
                                        <div>
                                            <p className="font-semibold text-slate-700">Created</p>
                                            <p className="text-slate-600">{new Date(quizData.createdAt).toLocaleString()}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                                        <MdAccessTime className="w-5 h-5 text-slate-600" />
                                        <div>
                                            <p className="font-semibold text-slate-700">Last Updated</p>
                                            <p className="text-slate-600">{new Date(quizData.updatedAt).toLocaleString()}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Rules Modal */}
            <RulesModal
                isOpen={showRulesModal}
                onClose={() => setShowRulesModal(false)}
                rulesUrl={quizData?.rules_url}
            />
        </div>
    );
};

export default QuizView;